import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Paperclip, PenSquare } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import AccountPicker from '../../components/admin/AccountPicker';
import BulkActionToolbar from '../../components/admin/BulkActionToolbar';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import StatusBadge from '../../components/admin/StatusBadge';
import EditRequestDetailModal from '../../components/admin/EditRequestDetailModal';
import AutoResolveBatchPanel from '../../components/admin/AutoResolveBatchPanel';
import AutoResolveBatchStrip from '../../components/admin/AutoResolveBatchStrip';
import AutoResolveBatchDialog from '../../components/admin/AutoResolveBatchDialog';
import { BucketChip } from '../../components/admin/AutoResolveBatchCard';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { AUTO_RESOLVE_BATCH_ENABLED } from '../../config/featureFlags';

const STATUS_META = {
  open: { label: 'Open', tone: 'accent' },
  in_progress: { label: 'In progress', tone: 'warning' },
  awaiting_reply: { label: 'Awaiting reply', tone: 'waiting' },
  resolved: { label: 'Resolved', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};

const CHANGE_TYPE_META = {
  text: { label: 'Text', tone: 'neutral' },
  image: { label: 'Image', tone: 'accent' },
  link: { label: 'Link', tone: 'neutral' },
  new_section: { label: 'New section', tone: 'success' },
  remove: { label: 'Remove', tone: 'danger' },
  other: { label: 'Other', tone: 'muted' },
};

// Same colours as the edit-request window. Older requests have no priority.
const PRIORITY_META = {
  low: { label: 'Low', tone: 'success' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'warning' },
  critical: { label: 'Critical', tone: 'danger' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Batch auto-resolve (EXPERIMENTAL): a row the header button sends — open,
// classified auto or review by the server, and not already in a running batch.
function batchReady(row) {
  const ar = row?.autoResolve;
  return Boolean(ar && !ar.locked && (ar.bucket === 'auto' || ar.bucket === 'review'));
}

const BATCH_BUTTON_HINT = 'Sends every open request the agent can handle to one agent run per school, and you review each school’s draft change before anything goes live.';

function previewDescription(description) {
  if (!description) return '';
  const single = description.replace(/\s+/g, ' ').trim();
  return single.length > 80 ? `${single.slice(0, 78)}…` : single;
}

export default function AdminEditRequests() {
  const [editRequests, setEditRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // The account filter's value lives in DataTable; mirrored here so the batch
  // auto-resolve panel (EXPERIMENTAL, flag) knows when ONE school is selected.
  const [accountFilter, setAccountFilter] = useState('');
  // Batch auto-resolve across schools (EXPERIMENTAL): the server switch is
  // known once the Batch runs strip has heard from its endpoint ('on' | 'off').
  const [batchMode, setBatchMode] = useState('unknown');
  const [batchDialog, setBatchDialog] = useState({ open: false, rows: [], key: 0 });
  const [stripReload, setStripReload] = useState(0);
  const [batchNotice, setBatchNotice] = useState('');

  // `quiet` reloads (after batches start or move on) keep the table on screen.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError('');
    try {
      const editReqData = await api.get('/api/admin/edit-requests');
      setEditRequests(editReqData.editRequests || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load edit requests');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ?request=<id> (from a edit request thread in Communications) opens that edit request.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('request');
  useEffect(() => {
    if (!linkedId || loading) return;
    const hit = editRequests.find((x) => x.id === linkedId);
    if (hit) setSelected(hit);
  }, [linkedId, loading, editRequests]);
  const closeDetail = () => {
    setSelected(null);
    if (linkedId) { const next = new URLSearchParams(searchParams); next.delete('request'); setSearchParams(next, { replace: true }); }
  };

  const onSaved = (updated) => {
    setEditRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  };

  const onDeleted = (id) => {
    setEditRequests((prev) => prev.filter((r) => r.id !== id));
    setSelected(null);
  };

  const toggleRow = (id) =>
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = (visibleIds) =>
    setCheckedIds((prev) => (visibleIds.every((id) => prev.includes(id))
      ? prev.filter((id) => !visibleIds.includes(id))
      : [...new Set([...prev, ...visibleIds])]));

  // Called by ConfirmDialog; it awaits this and shows any thrown error inline.
  const bulkResolve = async (ids) => {
      await api.post('/api/admin/edit-requests/bulk-resolve', { ids });
      setEditRequests((prev) => prev.map((r) => (ids.includes(r.id) ? { ...r, status: 'resolved' } : r)));
      setCheckedIds([]);
  };

  const resolvableIds = checkedIds.filter((id) => editRequests.find((r) => r.id === id)?.status !== 'resolved');
  const alreadyResolvedCount = checkedIds.length - resolvableIds.length;

  const batchOn = Boolean(AUTO_RESOLVE_BATCH_ENABLED) && batchMode === 'on';
  const batchReadyRows = useMemo(() => editRequests.filter(batchReady), [editRequests]);
  const onBatchAvailability = useCallback((on) => setBatchMode(on ? 'on' : 'off'), []);
  const reloadQuietly = useCallback(() => load({ quiet: true }), [load]);
  // The per-school panel started or acted on a batch: the strip and the list follow.
  const onPanelBatchesChanged = useCallback(() => {
    setStripReload((k) => k + 1);
    load({ quiet: true });
  }, [load]);
  // The dialog gets a snapshot of the rows (a new key each time it opens, so
  // it starts fresh); a list reload while it is open doesn't change it.
  const openBatchDialog = (rows) => {
    setBatchNotice('');
    setBatchDialog((prev) => ({ open: true, rows, key: prev.key + 1 }));
  };
  const closeBatchDialog = useCallback(() => setBatchDialog((prev) => ({ ...prev, open: false })), []);
  const onBatchesDispatched = ({ started, requestIds }) => {
    if (started) {
      setBatchNotice(`Started ${started === 1 ? '1 batch' : `${started} batches`} (${requestIds.length === 1 ? '1 request' : `${requestIds.length} requests`}). Follow ${started === 1 ? 'it' : 'them'} under Batch runs.`);
    }
    setCheckedIds((prev) => prev.filter((id) => !requestIds.includes(id)));
    setStripReload((k) => k + 1);
    load({ quiet: true });
  };

  const filters = useMemo(
    () => [
      {
        key: 'accountId',
        label: 'accounts',
        // The prod roster is ~1,900 accounts; a <select> fed by one page of
        // /api/admin/accounts only ever showed the first 50. AccountPicker
        // searches the full roster server-side.
        control: (value, setValue) => (
          <div className="min-w-[220px]">
            <AccountPicker
              value={value}
              onChange={(v) => { setValue(v); setAccountFilter(v || ''); }}
              emptyLabel="All accounts"
            />
          </div>
        ),
      },
      {
        key: 'status',
        label: 'statuses',
        options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
      },
      {
        key: 'changeType',
        label: 'change types',
        options: Object.entries(CHANGE_TYPE_META).map(([value, meta]) => ({ value, label: meta.label })),
      },
      {
        key: 'priority',
        label: 'priorities',
        options: Object.entries(PRIORITY_META).map(([value, meta]) => ({ value, label: meta.label })),
      },
    ],
    []
  );

  const columns = [
    {
      key: 'description',
      header: 'Request',
      sortable: false,
      render: (row) => (
        <div className="flex items-start gap-2.5">
          <PenSquare className="w-3.5 h-3.5 text-gray-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[11px] font-mono text-gray-500 truncate" title={row.pagePath}>
              {row.pagePath}
            </div>
            {/* Clients title their requests and scan by that title (it leads
                their own view); without it here, admins hunting for a request
                by name never find it — the description alone doesn't match. */}
            {row.title && (
              <div className="text-[13px] font-medium text-white mt-0.5 truncate" title={row.title}>
                {/* Same JS truncation as the description: in an auto-layout
                    table, an untruncated line sets the column's preferred
                    width and forces horizontal scroll on laptop windows. */}
                {previewDescription(row.title)}
              </div>
            )}
            <div className={`text-[13px] mt-0.5 truncate ${row.title ? 'text-gray-400 text-[12px]' : 'text-gray-200'}`} title={row.description}>
              {previewDescription(row.description)}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">
              {row.submittedBy?.name || 'Unknown'} · {row.accountName}
              {row.attachmentCount > 0 && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-gray-600">
                  · <Paperclip className="w-2.5 h-2.5" /> {row.attachmentCount}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'changeType',
      header: 'Type',
      sortable: true,
      render: (row) => {
        // Inline requests from the Visual Editor are published website updates.
        if (row.source === 'inline') return <StatusBadge tone="success">Website update</StatusBadge>;
        const meta = CHANGE_TYPE_META[row.changeType] || CHANGE_TYPE_META.other;
        const ar = AUTO_RESOLVE_BATCH_ENABLED ? row.autoResolve : null;
        if (!ar) return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
        // How batch auto-resolve would treat it (EXPERIMENTAL); the reason is the tooltip.
        return (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            <BucketChip bucket={ar.bucket} reason={ar.reason} short />
          </div>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (row) => {
        const meta = PRIORITY_META[row.priority];
        return meta
          ? <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
          : <span className="text-[12px] text-gray-600" aria-label="No priority set">—</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => {
        const meta = STATUS_META[row.status] || STATUS_META.open;
        return (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            {row.newReply && <StatusBadge tone="accent" className="!py-0.5">New reply</StatusBadge>}
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Opened',
      sortable: true,
      align: 'right',
      render: (row) => <span className="text-[12px] text-gray-400">{formatDate(row.createdAt)}</span>,
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Edit <span className="serif-acc accent-text">Requests</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Content change requests from school administrators. Click a row to update status or add internal notes.
          </p>
        </div>
        {batchOn && (
          <button
            type="button"
            onClick={() => openBatchDialog(batchReadyRows)}
            disabled={batchReadyRows.length === 0}
            title={BATCH_BUTTON_HINT}
            data-testid="batch-auto-resolve-open"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60"
          >
            <Layers className="w-4 h-4 text-[#B0FC38]" aria-hidden="true" />
            Batch auto-resolve
            <span className="px-1.5 py-0.5 rounded-full bg-[#B0FC38]/10 border border-white/10 text-[11px] font-semibold tabular-nums text-[#B0FC38]" data-testid="batch-ready-count">
              <span className="sr-only">, </span>{batchReadyRows.length}<span className="sr-only"> ready</span>
            </span>
          </button>
        )}
      </div>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {loadError}
        </div>
      )}

      {AUTO_RESOLVE_BATCH_ENABLED && (
        <AutoResolveBatchStrip
          reloadKey={stripReload}
          onAvailability={onBatchAvailability}
          onBatchesChanged={reloadQuietly}
        />
      )}

      {batchOn && batchNotice && (
        <p role="status" className="mb-4 px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[13px] text-gray-300" data-testid="batch-notice">
          {batchNotice}
        </p>
      )}

      {AUTO_RESOLVE_BATCH_ENABLED && accountFilter && (
        <AutoResolveBatchPanel
          key={accountFilter}
          accountId={accountFilter}
          accountName={editRequests.find((r) => r.accountId === accountFilter)?.accountName}
          onBatchesChanged={onPanelBatchesChanged}
        />
      )}

      <DataTable
        columns={columns}
        rows={editRequests}
        searchableKeys={['title', 'description', 'pagePath', 'accountName']}
        filters={filters}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        loading={loading}
        emptyMessage="No edit requests have been submitted yet."
        onRowClick={(row) => setSelected(row)}
        selectable
        selectedIds={checkedIds}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
      />

      <BulkActionToolbar
        selected={checkedIds}
        onClear={() => setCheckedIds([])}
        actions={[{
          key: 'resolve',
          // Only items not already resolved count toward the action; the
          // server skips resolved rows anyway, but the label should tell the
          // truth about what one click will do.
          label: `Mark ${resolvableIds.length} resolved${alreadyResolvedCount ? ` (${alreadyResolvedCount} already resolved)` : ''}`,
          disabled: resolvableIds.length === 0,
          hint: resolvableIds.length === 0 ? 'Everything selected is already resolved' : undefined,
          onRun: () => setConfirmBulk(true),
        },
        // Batch auto-resolve (EXPERIMENTAL, server switch): the dialog groups
        // the selection by school and ignores what can't be batched.
        ...(batchOn ? [{
          key: 'auto-resolve',
          label: 'Auto-resolve selected',
          onRun: () => openBatchDialog(editRequests.filter((r) => checkedIds.includes(r.id))),
        }] : [])]}
      />

      {batchOn && (
        <AutoResolveBatchDialog
          key={batchDialog.key}
          open={batchDialog.open}
          rows={batchDialog.rows}
          onClose={closeBatchDialog}
          onDispatched={onBatchesDispatched}
        />
      )}

      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Resolve ${resolvableIds.length} edit request${resolvableIds.length === 1 ? '' : 's'}?`}
        description={`This marks ${resolvableIds.length === 1 ? 'it' : 'them all'} resolved and sends the school one email listing ${resolvableIds.length === 1 ? 'it' : 'every item'}.${alreadyResolvedCount ? ` ${alreadyResolvedCount} already-resolved ${alreadyResolvedCount === 1 ? 'item is' : 'items are'} skipped.` : ''}`}
        confirmLabel="Resolve and notify"
        onConfirm={() => bulkResolve(resolvableIds)}
      />

      <EditRequestDetailModal
        open={Boolean(selected)}
        editRequest={selected}
        onClose={closeDetail}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </div>
  );
}
